import { useEffect, useRef } from 'react';
import { ArrowRight } from 'lucide-react';
import { Link } from 'react-router-dom';

import { HomeFeatureCard } from '@/features/home/components/HomeFeatureCard';
import { LabHomeCard } from '@/features/labs/LabHomeCard';
import { track } from '@/lib/analytics';
import { useMembershipOffer, type MembershipOffer } from '@/lib/api/membershipHooks';
import { repOfferTags } from './membershipLinks';

function dollars(cents: number): string {
  const value = cents / 100;
  return value % 1 === 0 ? `$${value.toFixed(0)}` : `$${value.toFixed(2)}`;
}

/**
 * The Home blood-test slot, and the portal membership A/B
 * (`portal-membership-v1`): eligible patients in variant-a see the System
 * Membership offer here instead of the blood-test card; control keeps the
 * card. Exposure is counted for both arms once eligibility is known. A
 * patient a rep sent the offer to (their link, or a View-as-patient session)
 * sees it whatever their arm; those are tagged, never counted as exposure.
 */
export function MembershipHomeSlot() {
  const { data: offer, isPending, isError } = useMembershipOffer();
  const counted = useRef(false);
  useEffect(() => {
    // No arm yet (e.g. the experiment was just published and not assigned): not an exposure.
    if (
      counted.current ||
      !offer?.eligible ||
      offer.source !== 'portal' ||
      !offer.experiment_key ||
      !offer.variant_key
    )
      return;
    counted.current = true;
    track('$experiment_viewed', {
      experiment_key: offer.experiment_key,
      variant_key: offer.variant_key,
      source: 'home_membership_card',
    });
  }, [offer]);
  // Brief: the offer decides which card fills the slot, so neither flashes.
  if (isPending && !isError) return null;
  if (offer?.available) return <MembershipOfferHomeCard offer={offer} />;
  return <LabHomeCard />;
}

const actionClass =
  'min-h-44 flex-1 flex justify-between items-center gap-10 px-15 py-15 font-body text-14 leading-130 text-white bg-black hover:bg-[#1a1a1a]';

function MembershipOfferHomeCard({ offer }: { offer: MembershipOffer }) {
  const viewed = useRef(false);
  const props = {
    surface: 'home',
    source: offer.source,
    experiment_key: offer.experiment_key,
    variant_key: offer.variant_key,
  };
  useEffect(() => {
    if (viewed.current) return;
    viewed.current = true;
    track('membership_offer_viewed', { ...props, ...repOfferTags(offer.source) });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  const tests = Math.round(12 / offer.lab_interval_months);
  const perks = [
    `${offer.perk_percent}% off your next renewal when you join`,
    `${offer.discount_percent}% off every renewal, new treatment and Shop order`,
    `${tests} at-home blood tests a year, included (a ${dollars(offer.lab_annual_value_cents)} value)`,
  ];
  return (
    <HomeFeatureCard
      label="System Membership"
      meta={`${dollars(offer.price_cents)}/yr`}
      actions={
        <Link
          className={actionClass}
          to="/app/membership"
          onClick={() => track('membership_offer_clicked', props)}
        >
          Become a member
          <ArrowRight size={16} aria-hidden />
        </Link>
      }
    >
      {/* The member card photo on the left, as on the intake offer. */}
      <div className="grid grid-cols-[8.5rem_minmax(0,1fr)] md:grid-cols-[13rem_minmax(0,1fr)] items-stretch">
        <div className="relative overflow-hidden bg-[#b9c3d6]">
          <img
            src="/images/membership/member-card-photo.webp"
            alt="System member card"
            className="absolute inset-0 w-full h-full object-cover object-center"
          />
        </div>
        <div className="min-w-0 px-15 py-20">
          <h2 className="font-mono uppercase text-20 md:text-24 leading-110 text-white">
            Half off your next renewal.
          </h2>
          <ul className="mt-12 flex flex-col gap-8">
            {perks.map((perk) => (
              <li key={perk} className="font-body text-14 md:text-15 text-white/75 leading-140 flex gap-8">
                <span aria-hidden className="text-[#CFE3D6]">
                  ✓
                </span>
                {perk}
              </li>
            ))}
          </ul>
        </div>
      </div>
    </HomeFeatureCard>
  );
}
